import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NOTE_TEMPLATES } from '../../../constants/noteTemplates';
import { ChevronDown, AlertTriangle, ClipboardList, Send, CheckCircle, XCircle, Loader, ClipboardCheck, Plus } from 'lucide-react';
import { useMobileDetection, getButtonClasses } from '../../../hooks/useMobileUtils';
import { useHasAnyPermission } from '../../../hooks/usePermission';
import { ChartingProps, Entry, HistoricalVisit } from './types';
import { useChartingData, isInlineEntryError } from './hooks/useChartingData';
import { useFieldMode } from '../../../hooks/useOfflineDevice';
import AddEntrySheet from './components/AddEntrySheet';
import { getEntryTitle } from './utils';
import { clockLabel } from './model/entryMeta';
import EntryContent from './components/EntryContent';
import EntryForm from './components/EntryForm';
import NewEntryForm from './components/NewEntryForm';
import EntryList from './components/EntryList';
import QuickAddMenu from './components/QuickAddMenu';
import { ChartFormContext, ChartFormState } from './ChartFormContext';
import { StaffDirectoryContext } from './StaffDirectoryContext';
import { useStaffDirectory } from './hooks/useStaffDirectory';
import Dialog from '../../common/Dialog';
import PrimaryButton from '../../common/PrimaryButton';
import PrimaryButtonLight from '../../common/PrimaryButtonLight';
import { DocStatusPill } from '../../common/Pills';
import { MobilePrimaryButton, MobilePrimaryButtonLight } from '../../common/MobileButtons';
import ActiveVisitChart from './components/ActiveVisitChart';
import HistoricalVisitsView from './components/HistoricalVisitsView';
import { fetchVisitHistory, fetchVisitById } from '../../../services/patientmr/visits.service';
import ReviewSidebar from './components/ReviewSidebar';
import { REVIEW_SHORTCUTS, useReviewShortcuts } from './hooks/useReviewShortcuts';
import { useMediaQuery, WIDE_REVIEW_QUERY } from '../../../hooks/useMediaQuery';
import { useTwoPane } from '../../../hooks/useTwoPane';
import { ReviewQueue } from '../../../services/patientmr/chartReview.service';
import { openPatientChartById } from '../../../constants/navigationEvents';
import { finalizeChart, submitChart, reopenChart, startQAChart, fetchChartEntriesWithStatus } from '../../../services/patientmr/charting.service';
import ReasonDialog from '../../common/ReasonDialog';
import { useToast } from '../../common/Toast';
import { describeApiError, hasApiErrorCode } from '../../../utils/apiErrors';
import { isApiError } from '../../../dtos/common/api.dto';
import DraftStatusChip from './components/DraftStatusChip';
import { ObservedTimeContext } from './ObservedTimeContext';
import ChartIntegrityPanel from './components/ChartIntegrityPanel';
import { useAuth } from '../../../context/authcontext';
import type { PermissionString } from '../../../types/permissions';
import { Visit } from '../../../types/patientmr/visit';
import Banner from '../../common/Banner';
import ConfirmDialog from '../../common/ConfirmDialog';
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard';
import { useChartFormAutosave } from '../../../hooks/useChartFormAutosave';
import ChartDraftBanner, { editDraftKey } from './components/ChartDraftBanner';
import SubmitChartSheet from './components/SubmitChartSheet';
import { disciplineLabel } from '../../../constants/disciplines';
import { usePatientPrograms } from '../programs/usePatientPrograms';
import { ProgramsService } from '../../../services/patientmr/programs.service';

/** Friendly text for domain errors (CHART_LOCKED, NOT_ENTRY_AUTHOR, ...), the server text otherwise */
const describeApiErrorOrMessage = (error: unknown, fallback: string): string => {
  if (isApiError(error) && error.domainCode) return describeApiError(error, fallback);
  return error instanceof Error && error.message ? error.message : fallback;
};

const Charting: React.FC<ChartingProps> = ({ expanded, toggleExpanded, patientId, activeVisitId, visitWindow, workspace = false, activeVisitStatus, onReviewDone, onOpenVisit, renderActionBar, openVisitId, onOpenVisitHandled }) => {
  const [expandedVisit, setExpandedVisit] = useState<string | number | null>(null);
  const [selectedEntryType, setSelectedEntryType] = useState<string | null>(null);
  const [isAddingNewEntry, setIsAddingNewEntry] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null); // State for API error message
  const [showActiveVisitChart, setShowActiveVisitChart] = useState<boolean>(true); // Default to showing Active Visit Chart if available
  const [historicalVisits, setHistoricalVisits] = useState<HistoricalVisit[]>([]);
  const [loadingVisits, setLoadingVisits] = useState<boolean>(false);
  const [visitsError, setVisitsError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false); // Track save operations
  const [submitting, setSubmitting] = useState<boolean>(false); // Track submit operations
  const [reopening, setReopening] = useState<boolean>(false); // Track reopen operations
  
  // Permission checks for charting features
  const canReadCharting = useHasAnyPermission([
    'patientmr:phi:charting:read' as any,
    'patientmr:phi:charting:readall' as any
  ]);

  const canUpdateCharting = useHasAnyPermission([
    'patientmr:phi:charting:update' as any,
    'patientmr:phi:charting:updateall' as any
  ]);

  const isReadOnly = canReadCharting && !canUpdateCharting;

  // QA reviewers / supervisors can start QA on a submitted chart (same gate as assessments and care plans)
  const canQAChartOnline = useHasAnyPermission([
    'patientmr:qa' as PermissionString,
    'patientmr:phi:supervisor:finalize' as PermissionString,
    'patientmr:phi:supervisor:approve' as PermissionString
  ]);
  // Phase 16C: QA and reopen actions need a connection; the offline workspace hides them
  const needsConnection = workspace && typeof navigator !== 'undefined' && navigator.onLine === false;
  const canQAChart = canQAChartOnline && !needsConnection;

  const { showToast } = useToast();

  // Reason prompts (Phase 4): invalidate an entry, reject a submitted chart, reopen a complete chart
  const [invalidateTargetId, setInvalidateTargetId] = useState<string | number | null>(null);
  const [invalidating, setInvalidating] = useState<boolean>(false);
  const [reopenDialogMode, setReopenDialogMode] = useState<'reject' | null>(null);
  const [startingQA, setStartingQA] = useState<boolean>(false);
  // Phase 9C: the author's change to an entry written before the first submit is an amendment (reason)
  const [amendTargetId, setAmendTargetId] = useState<string | number | null>(null);

  // Phase 9C: supervisors add addenda and decide reopen requests; managers may ask to reopen
  const canAddAddendum = useHasAnyPermission(['patientmr:phi:supervisor:approve' as PermissionString]);
  const canSupervisorApprove = useHasAnyPermission(['patientmr:phi:supervisor:approve' as PermissionString]);
  const canSupervisorReject = useHasAnyPermission(['patientmr:phi:supervisor:reject' as PermissionString]);
  const isChartManager = useHasAnyPermission(['patientmr:phi:charting:updateall' as PermissionString]);
  const { user } = useAuth();
  
  // Pagination state for historical visits
  const [visitsPaginationToken, setVisitsPaginationToken] = useState<string | undefined>(undefined);
  const [hasMoreVisits, setHasMoreVisits] = useState<boolean>(false);
  const [loadingMoreVisits, setLoadingMoreVisits] = useState<boolean>(false);
  const [historicalVisitPage, setHistoricalVisitPage] = useState(1);
  const [loadedHistoricalVisits, setLoadedHistoricalVisits] = useState<HistoricalVisit[]>([]);
  // Phase 18U U15: 20 visits per page (was 5)
  const VISITS_PER_PAGE = 20;
  
  // New state for charting mode
  const [isChartingMode, setIsChartingMode] = useState<boolean>(false);
  const [selectedHistoricalVisit, setSelectedHistoricalVisit] = useState<HistoricalVisit | null>(null);
  
  // State for charting status
  const [chartingStatus, setChartingStatus] = useState<'DRAFT' | 'SUBMITTED' | 'QA' | 'COMPLETE'>('DRAFT');
  
  // Ref to track if visits have been loaded to prevent duplicate API calls
  const hasLoadedVisitsRef = useRef<string | null>(null);

  // Phase 18U U7: phones and field-mode tablets (coarse pointer) get the touch layout at any width
  const fieldMode = useFieldMode(true);
  const isMobile = useMobileDetection() || fieldMode;
  
  // Use the custom hook to manage charting data and functionality
  const {
    currentVisitEntries,
    historicalVisitEntries,
    hasActiveVisit,
    loadingEntries,
    editingEntryId,
    inPlaceEditingEntryId,
    selectedVisitId,
    patientMedications,
    chartMetaByVisit,
    observedTime,
    setObservedTime,
    observedError,
    vitalFormData,
    marFormData,
    feedFormData,
    notesFormData,
    woundFormData,
    incidentFormData,
    summaryFormData,
    measurementsFormData,
    respiratoryFormData,
    seizureFormData,
    outputFormData,
    supervisoryFormData,
    therapyFormData,
    aideTasksFormData,
    templateNoteFormData,
    setRespiratoryFormData,
    setSeizureFormData,
    setOutputFormData,
    setSupervisoryFormData,
    setTherapyFormData,
    setAideTasksFormData,
    setTemplateNoteFormData,
    visitEntries,
    lastGivenByMedication,
    chartVisit,
    chartWindow,
    serverFieldErrors,
    getFormErrors,
    copyLast,
    hasLast,
    prepareNewEntry,
    startFollowUp,
    changeEntryVisibility,
    setEditingEntryId,
    setInPlaceEditingEntryId,
    setSelectedVisitId,
    setVitalFormData,
    setMarFormData,
    setFeedFormData,
    setNotesFormData,
    setWoundFormData,
    setIncidentFormData,
    setSummaryFormData,
    setMeasurementsFormData,
    submitEntry,
    handleFlagEntry,
    handleInvalidateEntry,
    resetFormData,
    populateFormData,
    loadHistoricalVisitEntries,
    loadEntries
  } = useChartingData(patientId, activeVisitId, visitWindow);
  // Phase 18 M5: entry authors and F21 supervised staff
  const staffDirectory = useStaffDirectory(patientId);
  // Phase 20F: the palliative care programme adds the symptom entry to nursing visits
  const { programs: patientPrograms } = usePatientPrograms(patientId);
  const palliativeActive = ProgramsService.palliativeActive(patientPrograms);

  // ── Phase 18 M3: the visit just ended stays here for "Review and sign" ──────────
  const reviewMode = !isChartingMode && activeVisitStatus === 'DOCPENDING' && Boolean(activeVisitId);
  const activeEntryCount = currentVisitEntries.filter((e) => !e.invalidated).length;
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const openSubmitConfirm = () => { setSubmitError(null); setSubmitConfirmOpen(true); };
  // Phase 18U U6: the Add sheet; U4: the entry just saved; copy-last chosen from the sheet
  const [addSheetOpen, setAddSheetOpen] = useState(false);
  const [highlightEntryId, setHighlightEntryId] = useState<string | number | null>(null);
  const [copiedType, setCopiedType] = useState<string | null>(null);
  // The saved-entry highlight is one-off: cleared so a later remount of the row does not flash again
  useEffect(() => {
    if (highlightEntryId === null) return undefined;
    const t = setTimeout(() => setHighlightEntryId(null), 2500);
    return () => clearTimeout(t);
  }, [highlightEntryId]);
  // Phase 18U U17: approve from the keyboard asks first; the shortcut list
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  // Phase 18U U1 (U-Q2): on a phone or tablet the patient page sends the current visit to the visit screen
  const chartsOnVisitScreen = Boolean(onOpenVisit) && !workspace;
  // Phase 18U U14: desktop review in two columns (timeline | sticky status, actions, integrity)
  const wideScreen = useMediaQuery(WIDE_REVIEW_QUERY);
  // Phase 18U U18: a landscape field-mode tablet charts side by side on the visit screen
  const sideBySide = useTwoPane(fieldMode) && workspace;

  // ── Phase 18 M10: new-entry form text autosaved per visit + entry type ────────
  // Entry types without a row here are not autosaved (add [value, setter] to include one)
  const autosaveForms: Record<string, [unknown, (v: never) => void]> = {
    vitals: [vitalFormData, setVitalFormData],
    mar: [marFormData, setMarFormData],
    feed: [feedFormData, setFeedFormData],
    notes: [notesFormData, setNotesFormData],
    wound: [woundFormData, setWoundFormData],
    incident: [incidentFormData, setIncidentFormData],
    summary: [summaryFormData, setSummaryFormData],
    measurements: [measurementsFormData, setMeasurementsFormData],
    respiratory: [respiratoryFormData, setRespiratoryFormData],
    seizure: [seizureFormData, setSeizureFormData],
    output: [outputFormData, setOutputFormData],
    supervisory: [supervisoryFormData, setSupervisoryFormData],
    therapy: [therapyFormData, setTherapyFormData],
    aidetasks: [aideTasksFormData, setAideTasksFormData],
    // Phase 20E: the note templates share one form state
    ...Object.fromEntries(NOTE_TEMPLATES.map((t) => [t.entryType.toLowerCase(), [templateNoteFormData, setTemplateNoteFormData]])),
  };
  // Phase 18U U9: edits are kept too, under their own key (edit~<entryId>), so they never mix with a new entry's draft
  const editingEntry = inPlaceEditingEntryId !== null
    ? [...currentVisitEntries, ...Object.values(historicalVisitEntries).flat()].find((en) => String(en.id) === String(inPlaceEditingEntryId))
    : undefined;
  const openFormType = editingEntry ? editingEntry.type : isAddingNewEntry ? selectedEntryType : null;
  const autosaveFormType = openFormType && autosaveForms[openFormType] ? openFormType : null;
  const autosaveType = autosaveFormType ? (editingEntry ? editDraftKey(editingEntry.id) : autosaveFormType) : null;
  const autosaveVisitId = selectedVisitId === 'current' ? activeVisitId : String(selectedVisitId || '');
  const chartAutosave = useChartFormAutosave<unknown>(
    autosaveVisitId || null,
    autosaveType,
    autosaveFormType ? autosaveForms[autosaveFormType][0] : null,
    (v) => { if (autosaveFormType) autosaveForms[autosaveFormType][1](v as never); },
    { enabled: !isReadOnly }
  );
  const unsavedGuard = useUnsavedChangesGuard(chartAutosave.isDirty);

  // Function to format Visit data to HistoricalVisit format
  const formatVisitToHistoricalVisit = (visit: Visit): HistoricalVisit => {
    // Format the date from startTime
    const date = new Date(visit.startTime);
    const formattedDate = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });

    // Create a descriptive visit type
    let type = 'Visit';
    
    // Use discipline and status to create a more descriptive type
    if (visit.discipline) {

      
      // Phase 20A: registry label (an LVN or PTA visit shows its discipline)
      const disciplineName = disciplineLabel(visit.discipline);
      
      type = `${disciplineName} Visit`;
    } else {
      // Fallback to notes or status-based type
      if (visit.notes) {
        type = visit.notes;
      } else if (visit.status === 'INPROGRESS') {
        type = 'Active Visit';
      } else if (visit.status === 'BILLED' || visit.status === 'READYFORBILLING' || visit.status === 'COMPLETE' || visit.status === 'DOCPENDING') {
        type = 'Completed Visit';
      } else if (visit.status === 'CANCELLED') {
        type = 'Cancelled Visit';
      }
    }

    return {
      id: visit.id,
      date: formattedDate,
      type: type,
      status: visit.status,
      maxIncidentSeverity: visit.maxIncidentSeverity,
      chartingStatus: (visit as { chartingStatus?: string }).chartingStatus,
      providerName: (visit as { providerName?: string }).providerName,
      discipline: visit.discipline ? disciplineLabel(visit.discipline) : undefined
    };
  };

  // Load historical visits when component mounts or patientId changes
  useEffect(() => {
    const loadVisits = async () => {
      if (!patientId) return;
      
      // Skip if already loaded for this patient or if currently loading
      if (hasLoadedVisitsRef.current === patientId || loadingVisits) {
        return;
      }
      
      try {
        setLoadingVisits(true);
        setVisitsError(null);
        hasLoadedVisitsRef.current = patientId; // Mark as loading immediately to prevent duplicate calls
        const response = await fetchVisitHistory(patientId, VISITS_PER_PAGE); // Load initial 5 visits
        
        // Sort visits by startTime (most recent first) and convert to HistoricalVisit[] format
        const formattedVisits = response.visits
          .sort((a, b) => {
            // Sort by startTime in descending order (most recent first)
            const timeA = new Date(a.startTime).getTime();
            const timeB = new Date(b.startTime).getTime();
            return timeB - timeA;
          })
          .map(formatVisitToHistoricalVisit);
        setHistoricalVisits(formattedVisits);
        setLoadedHistoricalVisits(formattedVisits);
        setHistoricalVisitPage(1);
        
        // Set pagination state
        setVisitsPaginationToken(response.paginationToken);
        setHasMoreVisits(response.hasMore);
      } catch (error) {
        console.error('Error fetching visit history:', error);
        setVisitsError(error instanceof Error ? error.message : 'Failed to load visit history');
        setHistoricalVisits([]); // Set empty array on error
        setLoadedHistoricalVisits([]);
        setHistoricalVisitPage(1);
        hasLoadedVisitsRef.current = null; // Reset on error so it can be retried
      } finally {
        setLoadingVisits(false);
      }
    };

    // Only load visits when expanded
    if (expanded && patientId) {
      loadVisits();
    }
    
    // Reset when collapsed
    if (!expanded) {
      hasLoadedVisitsRef.current = null;
      setHistoricalVisits([]);
      setLoadedHistoricalVisits([]);
      setHistoricalVisitPage(1);
      setVisitsPaginationToken(undefined);
      setHasMoreVisits(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, patientId, VISITS_PER_PAGE]); // loadingVisits intentionally excluded to avoid circular dependency

  // Fetch the next cursor page when numbered pagination moves beyond loaded data.
  const handleHistoricalVisitPageChange = async (page: number) => {
    if (loadingMoreVisits || page < 1) return;

    const loadedPageCount = Math.ceil(loadedHistoricalVisits.length / VISITS_PER_PAGE);
    if (page <= loadedPageCount) {
      setHistoricalVisitPage(page);
      setHistoricalVisits(loadedHistoricalVisits.slice((page - 1) * VISITS_PER_PAGE, page * VISITS_PER_PAGE));
      return;
    }

    if (!patientId || !visitsPaginationToken || !hasMoreVisits) return;
    
    try {
      setLoadingMoreVisits(true);
      setVisitsError(null);
      
      const response = await fetchVisitHistory(patientId, VISITS_PER_PAGE, visitsPaginationToken);
      
      // Sort new visits by startTime (most recent first) and convert to HistoricalVisit[] format
      const formattedVisits = response.visits
        .sort((a, b) => {
          // Sort by startTime in descending order (most recent first)
          const timeA = new Date(a.startTime).getTime();
          const timeB = new Date(b.startTime).getTime();
          return timeB - timeA;
        })
        .map(formatVisitToHistoricalVisit);

      if (formattedVisits.length === 0) {
        const lastPage = Math.max(1, Math.ceil(loadedHistoricalVisits.length / VISITS_PER_PAGE));
        setHasMoreVisits(false);
        setVisitsPaginationToken(undefined);
        setHistoricalVisitPage(lastPage);
        setHistoricalVisits(loadedHistoricalVisits.slice((lastPage - 1) * VISITS_PER_PAGE, lastPage * VISITS_PER_PAGE));
        return;
      }
      
      const updatedVisits = [...loadedHistoricalVisits, ...formattedVisits];
      setLoadedHistoricalVisits(updatedVisits);
      setHistoricalVisits(updatedVisits.slice((page - 1) * VISITS_PER_PAGE, page * VISITS_PER_PAGE));
      setHistoricalVisitPage(page);
      
      // Update pagination state
      setVisitsPaginationToken(response.paginationToken);
      setHasMoreVisits(response.hasMore);
    } catch (error) {
      console.error('Error loading more visits:', error);
      setVisitsError(error instanceof Error ? error.message : 'Failed to load more visits');
    } finally {
      setLoadingMoreVisits(false);
    }
  };

  // Remove the scroll position preservation code since we're not expanding in place anymore
  const handleExpandVisit = useCallback(async (visit: HistoricalVisit) => {
    if (!patientId) return;
    
    try {
      // Single API call - loadHistoricalVisitEntries now returns the charting status
      const status = await loadHistoricalVisitEntries(patientId, visit.id, false);
      
      // Set the charting status if returned (won't be null for new loads)
      if (status) {
        setChartingStatus(status);
      }
    } catch (error) {
      console.error('Error loading visit data:', error);
      setChartingStatus('DRAFT');
    }
    
    // Set the selected visit and transition to charting mode
    setSelectedHistoricalVisit(visit);
    setSelectedVisitId(visit.id);
    setIsChartingMode(true);
  }, [loadHistoricalVisitEntries, patientId, setSelectedVisitId]);
  
  // Phase 18U U16: the review queue opened this patient for one visit: show that chart
  useEffect(() => {
    if (!openVisitId || !patientId) return undefined;
    let live = true;
    fetchVisitById(patientId, openVisitId)
      .then((visit) => { if (live) handleExpandVisit(formatVisitToHistoricalVisit(visit as unknown as Visit)); })
      .catch(() => { if (live) handleExpandVisit({ id: openVisitId, date: '', type: 'Visit' }); })
      .finally(() => onOpenVisitHandled?.());
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per requested visit
  }, [openVisitId, patientId]);

  // Function to load charting status (kept for active visit)
  const loadChartingStatus = async (visitId: string | number) => {
    if (!patientId) return;
    
    try {
      const response = await fetchChartEntriesWithStatus(patientId, String(visitId));
      const status = response.chartingStatus as 'DRAFT' | 'SUBMITTED' | 'QA' | 'COMPLETE';
      setChartingStatus(status || 'DRAFT');
    } catch (error) {
      console.error('Error loading charting status:', error);
      setChartingStatus('DRAFT'); // Default to DRAFT on error
    }
  };
  
  // Load charting status when activeVisitId changes
  useEffect(() => {
    if (activeVisitId && patientId && !isChartingMode) {
      loadChartingStatus(activeVisitId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVisitId, patientId, isChartingMode]); // loadChartingStatus intentionally excluded to avoid circular dependency
  
  // Function to exit charting mode and return to visit list
  const handleExitChartingMode = useCallback(() => {
    setIsChartingMode(false);
    setSelectedHistoricalVisit(null);
    setSelectedVisitId('current');
    setExpandedVisit(null);
    // Reset any editing states
    setIsAddingNewEntry(false);
    setInPlaceEditingEntryId(null);
    setSelectedEntryType(null);
    resetFormData();
  }, [setSelectedVisitId, setInPlaceEditingEntryId, resetFormData]);
  
  const handleAddEntry = (type: string, visitId: string | number = selectedVisitId) => {
    // Cancel any existing editing to ensure only one item is being edited at a time
    if (inPlaceEditingEntryId) {
      cancelInPlaceEdit();
    }
    
    // Cancel any existing new entry creation
    if (isAddingNewEntry) {
      cancelAddingEntry();
    }
    
    setSelectedEntryType(type);
    setIsAddingNewEntry(true);
    setSelectedVisitId(visitId); // Set the visit ID for the new entry
    prepareNewEntry(); // Phase 18 M8: observed time defaults to now (the visit end after it)
  };

  /**
   * Cancels any in-place editing
   */
  const cancelInPlaceEdit = () => {
    setInPlaceEditingEntryId(null);
    resetFormData();
  };

  /**
   * Handles initiating the editing of an entry (in-place editing)
   * @param entry The entry to be edited
   */
  const handleEditEntry = (entry: Entry) => {
    // Cancel any existing in-place editing
    if (inPlaceEditingEntryId) {
      cancelInPlaceEdit();
    }
    
    // Cancel any in-progress new entry creation
    if (isAddingNewEntry) {
      cancelAddingEntry();
    }
    
    // Set the selected visit ID to match this entry's visit
    setSelectedVisitId(entry.visitId || 'current');
    
    // Reset form data to prevent stale data from previous edits
    resetFormData();
    
    // Set the selected entry type to match the entry's type
    setSelectedEntryType(entry.type);
    
    // First set the in-place editing ID so the form knows which entry to edit
    setInPlaceEditingEntryId(entry.id);
    
    // Then populate the form with this entry's data based on its type
    populateFormData(entry);
    
    console.log(`Starting in-place edit for entry: ${entry.id} of type: ${entry.type}`);
  };

  // Phase 9C chart metadata for the visit on screen (historical charting mode, else the active visit)
  const chartVisitId = String((isChartingMode ? selectedHistoricalVisit?.id : activeVisitId) || '');
  const currentChart = chartVisitId ? chartMetaByVisit[chartVisitId] : undefined;

  /** The entry was written before the chart's first submit, so a change to it is an amendment */
  const entryNeedsAmendReason = (entryId: string | number): boolean => {
    const firstSubmit = currentChart?.firstSubmittedAt;
    if (!firstSubmit) return false;
    const all = [...currentVisitEntries, ...Object.values(historicalVisitEntries).flat()];
    const entry = all.find((e) => String(e.id) === String(entryId));
    return Boolean(entry?.date && String(entry.date) <= String(firstSubmit));
  };

  const reloadChart = async () => {
    if (!patientId || !chartVisitId) return;
    if (isChartingMode) {
      const status = await loadHistoricalVisitEntries(patientId, chartVisitId, true);
      if (status) setChartingStatus(status);
    } else {
      await loadEntries(patientId, chartVisitId);
      await loadChartingStatus(chartVisitId);
    }
  };

  const cancelAddingEntry = () => {
    setIsAddingNewEntry(false);
    setEditingEntryId(null);
    setCopiedType(null);
    resetFormData();
  };

  /** Phase 18U U9: Cancel on an edit with changes asks first, then drops the edit's draft */
  const guardedCancelInPlaceEdit = () => unsavedGuard.confirmNavigation(() => {
    void chartAutosave.clear();
    cancelInPlaceEdit();
  });

  /** Phase 18U U4: confirm a save with the entry type and the time of care */
  const announceSaved = (type: string, result: { id?: string | number; observedAt?: string | null; date?: string; queued?: boolean } | undefined) => {
    const title = getEntryTitle(type);
    const at = clockLabel(result?.observedAt || result?.date || null);
    showToast(result?.queued ? `${title} saved on this device. It will sync when you are back online.` : `${title} saved${at ? ` · ${at}` : ''}`, { variant: 'success' });
    if (result?.id !== undefined && result?.id !== null) setHighlightEntryId(result.id);
  };

  /** Phase 18 M10: Cancel on a new-entry form asks first when it has text, then drops its draft */
  const guardedCancelAddingEntry = () => unsavedGuard.confirmNavigation(() => {
    void chartAutosave.clear();
    cancelAddingEntry();
  });

  const handleSubmitNewEntry = async () => {
    try {
      setSaving(true);
      if (!selectedEntryType) return;

      // Submit the entry
      const type = selectedEntryType;
      const result = await submitEntry(type, editingEntryId);
      await chartAutosave.clear(); // M10: saved to the chart, so the draft goes

      // Reset form and editing state
      setIsAddingNewEntry(false);
      setEditingEntryId(null);
      setCopiedType(null);
      resetFormData();
      announceSaved(type, result as Parameters<typeof announceSaved>[1]);
    } catch (error) {
      console.error('Error submitting entry');
      // Phase 18U U5: field and observed-time problems are shown in the form, not in a dialog
      if (!isInlineEntryError(error)) setApiError(describeApiErrorOrMessage(error, 'Failed to save entry. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  /**
   * Saves the changes made to an entry that's being edited in-place
   * @param entryId The ID of the entry being edited
   */
  const handleSaveInPlaceEdit = async (entryId: string | number, amendReason?: string) => {
    // Phase 9C: an entry written before the chart was first submitted is amended with a reason
    if (!amendReason && entryNeedsAmendReason(entryId)) {
      setAmendTargetId(entryId);
      return;
    }
    try {
      setSaving(true);
      console.log('Saving in-place edit for entry:', entryId);
      
      if (!inPlaceEditingEntryId) {
        console.error('No entry is currently being edited');
        return;
      }
      
      // Ensure we're editing the correct entry
      if (String(inPlaceEditingEntryId) !== String(entryId)) {
        console.error(`Mismatch in entry IDs: trying to save ${entryId} but editing ${inPlaceEditingEntryId}`);
        return;
      }
      
      // Look for the entry being edited to get its type
      const allEntries = [...currentVisitEntries, ...Object.values(historicalVisitEntries).flat()];
      const entry = allEntries.find(e => String(e.id) === String(inPlaceEditingEntryId));
      
      if (!entry) {
        console.error('Entry not found for in-place edit');
        return;
      }
      
      const entryType = entry.type;

      // Submit the entry
      await submitEntry(entryType, entryId, amendReason);
      await chartAutosave.clear(); // U9: the edit's draft goes
      
      // Reset in-place editing state
      setAmendTargetId(null);
      setInPlaceEditingEntryId(null);
      resetFormData();
      showToast(amendReason ? 'Amendment saved.' : 'Changes saved.', { variant: 'success' });
      setHighlightEntryId(entryId);
    } catch (error) {
      console.error('Error saving in-place edit');
      if (!amendReason && hasApiErrorCode(error, 'REASON_REQUIRED')) {
        // The server treats this change as an amendment: ask for the reason and retry
        setAmendTargetId(entryId);
      } else {
        setAmendTargetId(null);
        // Phase 18U U5: field problems stay in the edit form
        if (!isInlineEntryError(error)) setApiError(describeApiErrorOrMessage(error, 'Failed to save changes. Please try again.'));
      }
    } finally {
      setSaving(false);
    }
  };

  // Wrapper for handleFlagEntry to handle errors
  const onFlagEntry = async (entryId: string | number) => {
    try {
      await handleFlagEntry(entryId);
    } catch (error) {
      console.error('Error flagging entry:', error);
      setApiError(error instanceof Error ? error.message : 'Failed to flag entry. Please try again.');
    }
  };
  
  // Invalidate asks for a reason first; the request is sent from the ReasonDialog
  const onInvalidateEntry = (entryId: string | number) => {
    setInvalidateTargetId(entryId);
  };

  const confirmInvalidateEntry = async (reason: string) => {
    if (invalidateTargetId === null) return;
    try {
      setInvalidating(true);
      await handleInvalidateEntry(invalidateTargetId, reason);
      setInvalidateTargetId(null);
      showToast('Entry invalidated.', { variant: 'success' });
    } catch (error) {
      console.error('Error invalidating entry');
      showToast(describeApiError(error, 'Failed to invalidate entry. Please try again.'), { variant: 'error' });
    } finally {
      setInvalidating(false);
    }
  };
  
  // Function to refresh historical data
  const refreshHistoricalData = async () => {
    // Reset expanded visit view
    setExpandedVisit(null);
    // Set selected visit ID to current
    setSelectedVisitId('current');
    
    // Refetch historical visit data from API
    if (patientId) {
      try {
        setLoadingVisits(true);
        setVisitsError(null);
        hasLoadedVisitsRef.current = null; // Reset to allow refresh
        const response = await fetchVisitHistory(patientId, VISITS_PER_PAGE); // Load initial 5 visits
        
        // Convert Visit[] to HistoricalVisit[] format
        const formattedVisits = response.visits.map(formatVisitToHistoricalVisit);
        setHistoricalVisits(formattedVisits);
        setLoadedHistoricalVisits(formattedVisits);
        setHistoricalVisitPage(1);
        
        // Reset pagination state
        setVisitsPaginationToken(response.paginationToken);
        setHasMoreVisits(response.hasMore);
        
        hasLoadedVisitsRef.current = patientId; // Mark as loaded again
        
        console.log('Historical visit data refreshed successfully');
      } catch (error) {
        console.error('Error refreshing visit history:', error);
        setVisitsError(error instanceof Error ? error.message : 'Failed to refresh visit history');
      } finally {
        setLoadingVisits(false);
      }
    }
  };
  
  // Function to handle New Chart button click
  const handleNewChartClick = () => {
    // Only show the active chart if there's an active visit
    if (hasActiveVisit) {
      setShowActiveVisitChart(true);
      
      // Scroll to the active visit chart
      const chartElement = document.querySelector('.active-visit-chart');
      if (chartElement) {
        chartElement.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If no active visit, show an error message
      setApiError('No active visit available. Please start a visit to enable charting.');
      
      // Clear the error after 5 seconds
      setTimeout(() => {
        setApiError(null);
      }, 5000);
    }
  };

  // Function to handle finalizing/submitting the chart
  const handleSubmitChart = async () => {
    if (!patientId) {
      setApiError('Patient ID is required to submit chart.');
      return;
    }

    const visitId = selectedHistoricalVisit?.id || activeVisitId;
    if (!visitId) {
      setApiError('Visit ID is required to submit chart.');
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError(null);
      await submitChart(patientId, String(visitId));

      // Update status locally
      setChartingStatus('SUBMITTED');

      // Phase 18 M3: signed from the "Review and sign" view
      if (submitConfirmOpen) {
        setSubmitConfirmOpen(false);
        showToast('Chart signed and submitted.', { variant: 'success' });
        onReviewDone?.();
      }

      // Show success message
      console.log('Chart submitted successfully for visit:', visitId);
      
      // Exit charting mode if we were in historical charting
      if (isChartingMode) {
        handleExitChartingMode();
      }
      
      // Optionally refresh data
      await refreshHistoricalData();
    } catch (error) {
      console.error('Error submitting chart');
      // VISIT_SUMMARY_REQUIRED, SUPERVISORY_NOTE_REQUIRED, ... (utils/apiErrors)
      const message = describeApiError(error, 'Failed to submit chart. Please try again.');
      if (submitConfirmOpen) setSubmitError(message);
      else setApiError(message);
    } finally {
      setSubmitting(false);
    }
  };

  // Reopen (or reject) the chart; the reason comes from the ReasonDialog
  const handleReopenChart = async (reason: string) => {
    if (!patientId) {
      setApiError('Patient ID is required to reopen chart.');
      return;
    }

    const visitId = selectedHistoricalVisit?.id || activeVisitId;
    if (!visitId) {
      setApiError('Visit ID is required to reopen chart.');
      return;
    }

    try {
      setReopening(true);
      await reopenChart(patientId, String(visitId), reason);

      // Update status locally
      setChartingStatus('DRAFT');
      setReopenDialogMode(null);
      const clinician = selectedHistoricalVisit?.providerName;
      showToast(clinician ? `Chart returned to ${clinician} for correction.` : 'Chart returned to the clinician for correction.', { variant: 'success' });

      // Reload charting status to confirm
      await loadChartingStatus(visitId);
    } catch (error) {
      console.error('Error reopening chart');
      showToast(describeApiError(error, 'Failed to reopen chart. Please try again.'), { variant: 'error' });
    } finally {
      setReopening(false);
    }
  };

  // Start QA review on a submitted chart (SUBMITTED -> QA)
  const handleStartQAChart = async () => {
    const visitId = selectedHistoricalVisit?.id || activeVisitId;
    if (!patientId || !visitId) {
      setApiError('Patient and visit are required to start QA.');
      return;
    }
    try {
      setStartingQA(true);
      await startQAChart(patientId, String(visitId));
      setChartingStatus('QA');
      showToast('QA review started.', { variant: 'success' });
    } catch (error) {
      console.error('Error starting chart QA');
      showToast(describeApiError(error, 'Failed to start QA. Please try again.'), { variant: 'error' });
    } finally {
      setStartingQA(false);
    }
  };

  // Function to handle approving/finalizing the chart
  const handleApproveChart = async () => {
    if (!patientId) {
      setApiError('Patient ID is required to approve chart.');
      return;
    }

    const visitId = selectedHistoricalVisit?.id || activeVisitId;
    if (!visitId) {
      setApiError('Visit ID is required to approve chart.');
      return;
    }

    try {
      setSubmitting(true);
      await finalizeChart(patientId, String(visitId));
      
      // Update status locally
      setChartingStatus('COMPLETE');
      
      // Show success message
      const nextInQueue = ReviewQueue.next(String(visitId));
      showToast(nextInQueue ? 'Chart approved. Opening the next chart.' : 'Chart approved.', { variant: 'success' });
      if (nextInQueue) openPatientChartById(nextInQueue.patientId, 'Charting', nextInQueue.visitId);
      
      // Exit charting mode if we were in historical charting
      if (isChartingMode) {
        handleExitChartingMode();
      }
      
      // Optionally refresh data
      await refreshHistoricalData();
    } catch (error) {
      console.error('Error approving chart');
      showToast(describeApiError(error, 'Failed to approve chart. Please try again.'), { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const renderEntryContent = (entry: Entry) => {
    return <EntryContent entry={entry} />;
  };

  /**
   * Renders the appropriate form for editing an entry
   * @param entryId The ID of the entry that's being edited
   * @returns The form component or null if this entry is not being edited
   */
  const renderSelectedForm = (entryId: number | string): React.ReactNode => {
    // Safety check - if we're not editing this exact entry, return null
    const isExactMatch = String(inPlaceEditingEntryId) === String(entryId);
    console.log(`renderSelectedForm for ${entryId}, editing ID: ${inPlaceEditingEntryId}, match: ${isExactMatch}`);
    
    if (!isExactMatch) {
      return null;
    }
    
    // Find the entry being edited to get its type and data
    const allEntries = [...currentVisitEntries, ...Object.values(historicalVisitEntries).flat()];
    const currentEntry = allEntries.find(e => String(e.id) === String(entryId));
    
    if (!currentEntry) {
      console.error(`Cannot find entry with id ${entryId} for editing`);
      return null;
    }
    
    console.log(`Rendering edit form for entry ${entryId} of type ${currentEntry.type}`);
    
    // Phase 18U U9: the edit sheet shows "Resume unsaved changes?" above the form
    return (
      <>
      {chartForm.editDraftNotice && <div className="mb-3">{chartForm.editDraftNotice}</div>}
      <EntryForm
        key={`edit-form-${entryId}`} // Ensure React recreates this component when the ID changes
        selectedEntryType={currentEntry.type}
        vitalFormData={vitalFormData}
        setVitalFormData={setVitalFormData}
        marFormData={marFormData}
        setMarFormData={setMarFormData}
        feedFormData={feedFormData}
        setFeedFormData={setFeedFormData}
        notesFormData={notesFormData}
        setNotesFormData={setNotesFormData}
        woundFormData={woundFormData}
        setWoundFormData={setWoundFormData}
        incidentFormData={incidentFormData}
        setIncidentFormData={setIncidentFormData}
        summaryFormData={summaryFormData}
        setSummaryFormData={setSummaryFormData}
        measurementsFormData={measurementsFormData}
        setMeasurementsFormData={setMeasurementsFormData}
        editingEntryId={inPlaceEditingEntryId}
        cancelAddingEntry={cancelInPlaceEdit}
        handleSubmitNewEntry={() => handleSaveInPlaceEdit(entryId)}
        isMobile={isMobile}
        patientMedications={patientMedications}
      />
      </>
    );
  };

  // Common action buttons component
  const ActionButtons = () => {
    // Phase 18 M3: also below the chart of the visit just ended (DOCPENDING)
    if (!isChartingMode && !reviewMode) return null;
    
    // Determine button visibility based on status
    const showSubmitButton = chartingStatus === 'DRAFT';
    // Phase 12 (§4.4): QA decisions need the QA permission, not just charting update
    const showRejectButton = (chartingStatus === 'SUBMITTED' || chartingStatus === 'QA') && canQAChart;
    const showApproveButton = (chartingStatus === 'SUBMITTED' || chartingStatus === 'QA') && canQAChart;
    // A COMPLETE chart is reopened by request and supervisor approval (ChartIntegrityPanel)
    const showStartQAButton = chartingStatus === 'SUBMITTED' && canQAChart;
    
    return (
      <div className="flex justify-end gap-2">
        {isMobile ? (
          <>
            {isChartingMode && <MobilePrimaryButtonLight
              type="button"
              onClick={() => unsavedGuard.confirmNavigation(handleExitChartingMode)}
              className="flex items-center gap-2 px-4 py-2 w-24 justify-center"
              disabled={submitting || reopening}
            >
              Cancel
            </MobilePrimaryButtonLight>}
            {!isReadOnly && showRejectButton && (
              <MobilePrimaryButton
                onClick={() => setReopenDialogMode('reject')}
                disabled={reopening}
                className="flex items-center gap-2 px-4 py-2 w-28 justify-center"
              >
                {reopening ? (
                  <Loader size={16} className="animate-spin" />
                ) : (
                  <>
                    <XCircle size={16} />
                    Return
                  </>
                )}
              </MobilePrimaryButton>
            )}
            {!isReadOnly && showStartQAButton && (
              <MobilePrimaryButton
                onClick={handleStartQAChart}
                disabled={startingQA}
                className="flex items-center gap-2 px-4 py-2 w-28 justify-center"
              >
                {startingQA ? (
                  <Loader size={16} className="animate-spin" />
                ) : (
                  <>
                    <ClipboardCheck size={16} />
                    Start QA
                  </>
                )}
              </MobilePrimaryButton>
            )}
            {!isReadOnly && showApproveButton && (
              <MobilePrimaryButton
                onClick={handleApproveChart}
                disabled={submitting}
                className="flex items-center gap-2 px-4 py-2 w-28 justify-center"
              >
                {submitting ? (
                  <Loader size={16} className="animate-spin" />
                ) : (
                  <>
                    <CheckCircle size={16} />
                    Approve
                  </>
                )}
              </MobilePrimaryButton>
            )}
            {!isReadOnly && showSubmitButton && (
              <MobilePrimaryButton
                onClick={openSubmitConfirm}
                disabled={submitting}
                className="flex items-center gap-2 px-4 py-2 w-24 justify-center"
              >
                {submitting ? (
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <>
                    <Send size={16} />
                    Submit
                  </>
                )}
              </MobilePrimaryButton>
            )}
          </>
        ) : (
          <>
            {isChartingMode && <PrimaryButtonLight
              onClick={() => unsavedGuard.confirmNavigation(handleExitChartingMode)}
              className="flex items-center gap-2 px-4 py-2 w-24 justify-center"
              disabled={submitting || reopening}
            >
              Cancel
            </PrimaryButtonLight>}
            {!isReadOnly && showRejectButton && (
              <PrimaryButton
                onClick={() => setReopenDialogMode('reject')}
                disabled={reopening}
                className="flex items-center gap-2 px-4 py-2 w-28 justify-center"
              >
                {reopening ? (
                  <>
                    <svg className="animate-spin mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 414 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Returning...
                  </>
                ) : (
                  <>
                    <XCircle size={16} />
                    Return
                  </>
                )}
              </PrimaryButton>
            )}
            {!isReadOnly && showStartQAButton && (
              <PrimaryButton
                onClick={handleStartQAChart}
                disabled={startingQA}
                className="flex items-center gap-2 px-4 py-2 w-28 justify-center"
              >
                {startingQA ? (
                  <>
                    <Loader size={16} className="animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <ClipboardCheck size={16} />
                    Start QA
                  </>
                )}
              </PrimaryButton>
            )}
            {!isReadOnly && showApproveButton && (
              <PrimaryButton
                onClick={handleApproveChart}
                disabled={submitting}
                className="flex items-center gap-2 px-4 py-2 w-28 justify-center"
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 414 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Approving...
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} />
                    Approve
                  </>
                )}
              </PrimaryButton>
            )}
            {!isReadOnly && showSubmitButton && (
              <PrimaryButton
                onClick={openSubmitConfirm}
                disabled={submitting}
                className="flex items-center gap-2 px-4 py-2 w-24 justify-center"
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Submit
                  </>
                )}
              </PrimaryButton>
            )}
          </>
        )}
      </div>
    );
  };

  // getStatusBadge removed — use <DocStatusPill status={chartingStatus} /> from common/Pills

  // Phase 18U U14: wide review layout, its time window and the next chart of the queue
  const wideReview = wideScreen && isChartingMode && !isMobile && !workspace;
  const clockOf = (iso?: string | null) => (iso ? new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '');
  const reviewTimeWindow = chartVisit?.startTime ? `${clockOf(chartVisit.startTime)}${chartVisit.endTime ? ` – ${clockOf(chartVisit.endTime)}` : ' · in progress'}` : undefined;
  const nextReview = isChartingMode && chartVisitId ? ReviewQueue.next(chartVisitId) : null;
  // Phase 18U U17: review shortcuts (A approve, R return, Esc close, J/K entries, ? help)
  const canDecideChart = (chartingStatus === 'SUBMITTED' || chartingStatus === 'QA') && canQAChart && !isReadOnly;
  useReviewShortcuts(wideReview, {
    onApprove: canDecideChart ? () => setApproveConfirmOpen(true) : undefined,
    onReturn: canDecideChart ? () => setReopenDialogMode('reject') : undefined,
    onClose: () => unsavedGuard.confirmNavigation(handleExitChartingMode),
    onHelp: () => setShortcutsOpen(true),
  });

  const integrityPanel = patientId && chartVisitId ? (
    <ChartIntegrityPanel
      patientId={patientId}
      visitId={chartVisitId}
      chartingStatus={chartingStatus}
      chart={currentChart}
      currentUserId={user?.id}
      canUpdateCharting={canUpdateCharting}
      canAddAddendum={canAddAddendum}
      canDecideReopen={canSupervisorApprove && canSupervisorReject}
      isManager={isChartManager}
      onChanged={reloadChart}
    />
  ) : null;

  // Phase 18U: Add sheet target, what can be added now, the open form's draft banner
  const addVisitId: string | number = isChartingMode && selectedHistoricalVisit ? selectedHistoricalVisit.id : 'current';
  const canAddNow = chartingStatus === 'DRAFT' && canUpdateCharting && !isReadOnly && (isChartingMode || hasActiveVisit)
    && !isAddingNewEntry && inPlaceEditingEntryId === null;
  const openAddSheet = () => { if (!isAddingNewEntry && inPlaceEditingEntryId === null) setAddSheetOpen(true); };
  const submitEntryCount = isChartingMode && selectedHistoricalVisit
    ? (historicalVisitEntries[selectedHistoricalVisit.id] || []).filter((en) => !en.invalidated).length
    : activeEntryCount;
  const formDraftNotice = chartAutosave.pendingDraft ? (
    <ChartDraftBanner
      pendingDraft={chartAutosave.pendingDraft}
      onResume={chartAutosave.resume}
      onDiscard={() => { void chartAutosave.discard(); }}
    />
  ) : null;

  // Phase 18: entry-form state and list actions read by EntryForm, EntryListItem and EntryContent
  const chartForm: ChartFormState = {
    respiratoryFormData,
    setRespiratoryFormData,
    seizureFormData,
    setSeizureFormData,
    outputFormData,
    setOutputFormData,
    supervisoryFormData,
    setSupervisoryFormData,
    therapyFormData,
    setTherapyFormData,
    aideTasksFormData,
    setAideTasksFormData,
    templateNoteFormData,
    setTemplateNoteFormData,
    patientId: patientId || undefined,
    palliative: palliativeActive,
    errorsFor: getFormErrors,
    serverFieldErrors,
    visitEntries,
    lastGivenByMedication,
    visit: chartVisit,
    visitWindow: chartWindow,
    copyLast,
    hasLast,
    // M7 "Record effect": a MAR follow-up of the PRN dose, prefilled
    recordEffect: (entry: Entry) => {
      handleAddEntry('mar');
      startFollowUp(entry);
    },
    canSuperviseVisibility: canAddAddendum,
    changeVisibility: changeEntryVisibility,
    chartEditable: chartingStatus === 'DRAFT' && canUpdateCharting && !isReadOnly,
    currentUserId: user?.id,
    // Phase 18U U6: the open form was prefilled from "Copy last" in the Add sheet
    copiedFromLast: copiedType,
    // Phase 18U U9: the edit form's "Resume unsaved changes?" banner
    editDraftNotice: inPlaceEditingEntryId !== null ? formDraftNotice : null
  };

  return (
    <ObservedTimeContext.Provider value={{ observedTime, setObservedTime, observedError }}>
    <StaffDirectoryContext.Provider value={staffDirectory}>
    <ChartFormContext.Provider value={chartForm}>
    <div className="bg-white md:rounded md:shadow overflow-hidden md:border-b md:border-gray-200 mb-0 md:mb-2">
      {/* Header Section: the accordion toggle on phones and field-mode tablets (Phase 18U U7);
          the visit screen has its own header */}
      {!workspace && (
        <h2 className={`${isMobile ? '' : 'md:hidden'} m-0 border-b border-slate-200`}>
          <button
            type="button"
            aria-expanded={expanded}
            onClick={toggleExpanded}
            className="flex w-full items-center justify-between bg-slate-50 px-4 py-4 text-left transition-colors hover:bg-slate-100 active:bg-slate-200"
          >
            <span className="flex items-center gap-3">
              <span className="flex items-center text-lg font-semibold text-slate-800">
                <ClipboardList size={20} className="mr-3 text-slate-600" aria-hidden="true" />
                {isChartingMode && selectedHistoricalVisit
                  ? `Charting - ${selectedHistoricalVisit.type} (${selectedHistoricalVisit.date})`
                  : 'Charting'}
              </span>
              {expanded && (isChartingMode || hasActiveVisit) && <DocStatusPill status={chartingStatus} />}
            </span>
            <ChevronDown size={20} aria-hidden="true" className={`transition-transform text-gray-500 ${expanded ? 'rotate-180' : ''}`} />
          </button>
        </h2>
      )}

      {/* Content Section (visible when expanded) */}
      <div className={`${
        expanded ? 'h-auto' : 'h-0 overflow-hidden'
      } transition-all duration-300`}>
        <div className="p-3 md:p-4">
          {/* Top Action Buttons in Charting Mode (the wide review keeps them in its sidebar) */}
          {isChartingMode && !wideReview && (
            <div className="mb-4 flex justify-end">
              <ActionButtons />
            </div>
          )}

          {/* Phase 9C: return comment, author notices, signature, reopen, addendum, versions */}
          {!wideReview && (isChartingMode || (showActiveVisitChart && hasActiveVisit && !chartsOnVisitScreen)) && integrityPanel}
          
          {/* Phase 18 M10: unsaved form text kept on this device */}
          {!isReadOnly && !inPlaceEditingEntryId && !isAddingNewEntry && !chartsOnVisitScreen && (
            <div className="mb-3">
              <ChartDraftBanner
                visitDrafts={chartAutosave.visitDrafts}
                onOpenDraft={(type) => {
                  chartAutosave.resumeNext(type);
                  handleAddEntry(type, isChartingMode && selectedHistoricalVisit ? selectedHistoricalVisit.id : 'current');
                }}
              />
            </div>
          )}

          <div className="mt-4">
            {isChartingMode ? (
              // Historical Charting Mode - Using same components/styling as ActiveVisitChart
              <div className={wideReview ? 'grid grid-cols-[minmax(0,1fr)_22rem] items-start gap-6' : ''} data-testid={wideReview ? 'review-layout' : undefined}>
              <div className="min-w-0">
                {selectedHistoricalVisit && (
                  <div className="mb-6 border-b pb-6">
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-3">
                        <div className="text-base font-semibold text-slate-800">
                          {selectedHistoricalVisit.type} - {selectedHistoricalVisit.date}
                        </div>
                      </div>
                      
                      {/* Quick Entry Buttons - Same as ActiveVisitChart (Phase 18 M8 order) */}
                      {isMobile && canUpdateCharting && (
                        <button
                          type="button"
                          onClick={openAddSheet}
                          className="mb-2 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-slate-800 text-base font-semibold text-white"
                          data-testid="open-add-sheet"
                        >
                          <Plus size={20} aria-hidden="true" />Add to chart
                        </button>
                      )}
                      {!isMobile && !inPlaceEditingEntryId && !isAddingNewEntry && canUpdateCharting && (
                        <QuickAddMenu
                          onAdd={(type) => handleAddEntry(type, selectedHistoricalVisit.id)}
                          disabled={inPlaceEditingEntryId !== null || isAddingNewEntry || chartingStatus !== 'DRAFT'}
                          isMobile={isMobile}
                          discipline={chartVisit?.discipline}
                          visitType={chartVisit?.visitType}
                          entries={historicalVisitEntries[selectedHistoricalVisit.id] || []}
                          palliative={palliativeActive}
                        />
                      )}
                    </div>

                    {/* New Entry Form - Same component as ActiveVisitChart */}
                    {isAddingNewEntry && selectedEntryType && (
                      <div className="mb-4">
                        <NewEntryForm
                          selectedEntryType={selectedEntryType}
                          vitalFormData={vitalFormData}
                          setVitalFormData={setVitalFormData}
                          marFormData={marFormData}
                          setMarFormData={setMarFormData}
                          feedFormData={feedFormData}
                          setFeedFormData={setFeedFormData}
                          notesFormData={notesFormData}
                          setNotesFormData={setNotesFormData}
                          woundFormData={woundFormData}
                          setWoundFormData={setWoundFormData}
                          incidentFormData={incidentFormData}
                          setIncidentFormData={setIncidentFormData}
                          summaryFormData={summaryFormData}
                          setSummaryFormData={setSummaryFormData}
                          measurementsFormData={measurementsFormData}
                          setMeasurementsFormData={setMeasurementsFormData}
                          editingEntryId={editingEntryId}
                          cancelAddingEntry={guardedCancelAddingEntry}
                          handleSubmitNewEntry={handleSubmitNewEntry}
                          isMobile={isMobile}
                          onCancel={guardedCancelAddingEntry}
                          onSave={handleSubmitNewEntry}
                          saving={saving}
                          patientMedications={patientMedications}
                          isReadOnly={isReadOnly}
                          draftNotice={formDraftNotice}
                          savedAt={chartAutosave.lastSavedAt}
                        />
                      </div>
                    )}
                    
                    {/* Historical Visit Entries - Same layout as ActiveVisitChart */}
                    <div className="space-y-3">
                      {historicalVisitEntries[selectedHistoricalVisit.id] &&
                       historicalVisitEntries[selectedHistoricalVisit.id].length > 0 ? (
                        <EntryList
                          entries={historicalVisitEntries[selectedHistoricalVisit.id]}
                          keyPrefix={`historical-${selectedHistoricalVisit.id}-entry`}
                          handleEditEntry={handleEditEntry}
                          handleFlagEntry={onFlagEntry}
                          handleInvalidateEntry={onInvalidateEntry}
                          inPlaceEditingEntryId={inPlaceEditingEntryId}
                          setInPlaceEditingEntryId={setInPlaceEditingEntryId}
                          renderEntryContent={renderEntryContent}
                          renderSelectedForm={renderSelectedForm}
                          cancelInPlaceEdit={guardedCancelInPlaceEdit}
                          handleSaveInPlaceEdit={handleSaveInPlaceEdit}
                          isMobile={isMobile}
                          saving={saving}
                          locked={chartingStatus !== 'DRAFT'}
                          visitEnded={currentChart?.visitStatus ? currentChart.visitStatus !== 'INPROGRESS' : true}
                          highlightEntryId={highlightEntryId}
                        />
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <p>No chart entries found for this visit.</p>
                          <p className="text-sm">Use the buttons above to add new entries.</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Bottom Action Buttons */}
                    {!wideReview && (
                    <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
                      <ActionButtons />
                    </div>
                    )}
                  </div>
                )}
              </div>
              {wideReview && selectedHistoricalVisit && (
                <ReviewSidebar
                  chartingStatus={chartingStatus}
                  title={[selectedHistoricalVisit.type, selectedHistoricalVisit.date].filter(Boolean).join(' · ')}
                  clinician={selectedHistoricalVisit.providerName}
                  timeWindow={reviewTimeWindow}
                  actions={ActionButtons()}
                  integrity={integrityPanel}
                  next={nextReview ? { label: `Next chart in the queue`, onOpen: () => openPatientChartById(nextReview.patientId, 'Charting', nextReview.visitId) } : null}
                />
              )}
              </div>
            ) : (
              // Default View - Active Visit Chart + Historical Visits List
              <div>
                {/* Display Active Visit Chart conditionally (when showActiveVisitChart is true AND hasActiveVisit is true) */}
                {/* VIS-10: entries kept on this device while offline */}
                <div className="mb-3">
                  <DraftStatusChip visitId={activeVisitId} onSynced={() => { if (patientId && activeVisitId) loadEntries(patientId, activeVisitId); }} />
                </div>
                {/* Phase 18 M3: "Review and sign" after the visit ends */}
                {chartsOnVisitScreen && hasActiveVisit && activeVisitId && (
                  <section className="mb-6 rounded-xl border-2 border-emerald-500 bg-emerald-50 p-4" data-testid="open-visit-card">
                    <div className="text-base font-semibold text-emerald-900">
                      {reviewMode ? 'Visit ended: review and sign the chart' : 'Visit in progress'}
                    </div>
                    <p className="mt-1 text-sm text-emerald-900">
                      {activeEntryCount} chart {activeEntryCount === 1 ? 'entry' : 'entries'}. Charting for this visit happens on the visit screen.
                    </p>
                    <button
                      type="button"
                      onClick={() => onOpenVisit?.(activeVisitId)}
                      className="mt-3 flex min-h-[52px] w-full items-center justify-center rounded-lg bg-emerald-700 text-base font-semibold text-white"
                    >
                      Open current visit
                    </button>
                  </section>
                )}
                {reviewMode && chartingStatus === 'DRAFT' && !chartsOnVisitScreen && (
                  <div className="mb-3">
                    <Banner
                      variant="warning"
                      title="Review and sign"
                      data-testid="review-and-sign-banner"
                      action={!isReadOnly ? (
                        <button type="button" onClick={openSubmitConfirm} className="min-h-[40px] rounded bg-slate-800 px-3 text-sm font-medium text-white hover:bg-slate-700">Review and submit</button>
                      ) : undefined}
                    >
                      The visit has ended. Check the {activeEntryCount} chart {activeEntryCount === 1 ? 'entry' : 'entries'}, add anything missing, then submit to sign the chart.
                    </Banner>
                  </div>
                )}
                {showActiveVisitChart && hasActiveVisit && !chartsOnVisitScreen && (
                  <div className="relative mb-6 active-visit-chart">
                    <ActiveVisitChart
                      hasActiveVisit={hasActiveVisit}
                      isMobile={isMobile}
                      inPlaceEditingEntryId={inPlaceEditingEntryId}
                      isAddingNewEntry={isAddingNewEntry}
                      handleAddEntry={handleAddEntry}
                      selectedEntryType={selectedEntryType}
                      vitalFormData={vitalFormData}
                      setVitalFormData={setVitalFormData}
                      marFormData={marFormData}
                      setMarFormData={setMarFormData}
                      feedFormData={feedFormData}
                      setFeedFormData={setFeedFormData}
                      notesFormData={notesFormData}
                      setNotesFormData={setNotesFormData}
                      woundFormData={woundFormData}
                      setWoundFormData={setWoundFormData}
                      incidentFormData={incidentFormData}
                      setIncidentFormData={setIncidentFormData}
                      summaryFormData={summaryFormData}
                      setSummaryFormData={setSummaryFormData}
                      measurementsFormData={measurementsFormData}
                      setMeasurementsFormData={setMeasurementsFormData}
                      editingEntryId={editingEntryId}
                      cancelAddingEntry={guardedCancelAddingEntry}
                      handleSubmitNewEntry={handleSubmitNewEntry}
                      loadingEntries={loadingEntries}
                      currentVisitEntries={currentVisitEntries}
                      handleEditEntry={handleEditEntry}
                      onFlagEntry={onFlagEntry}
                      onInvalidateEntry={onInvalidateEntry}
                      setInPlaceEditingEntryId={setInPlaceEditingEntryId}
                      renderEntryContent={renderEntryContent}
                      renderSelectedForm={renderSelectedForm}
                      cancelInPlaceEdit={guardedCancelInPlaceEdit}
                      handleSaveInPlaceEdit={handleSaveInPlaceEdit}
                      chartingStatus={chartingStatus}
                      saving={saving}
                      patientMedications={patientMedications}
                      isReadOnly={isReadOnly}
                      onOpenAdd={openAddSheet}
                      hideAddButton={Boolean(renderActionBar)}
                      draftNotice={formDraftNotice}
                      draftSavedAt={chartAutosave.lastSavedAt}
                      highlightEntryId={highlightEntryId}
                      sideBySide={sideBySide}
                    />
                  </div>
                )}
                {reviewMode && hasActiveVisit && !chartsOnVisitScreen && (
                  <div className="mb-6 border-t border-gray-200 pt-4" data-testid="review-actions">
                    {/* called, not mounted: a remount on every render would drop focus and clicks */}
                    {ActionButtons()}
                  </div>
                )}

                {/* Show visits error if there is one */}
                {!workspace && visitsError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex items-center">
                      <AlertTriangle className="h-4 w-4 text-red-600 mr-2" />
                      <span className="text-red-700 text-sm">{visitsError}</span>
                    </div>
                  </div>
                )}
                
                {/* Historical visits (not in the offline visit workspace, which charts one visit) */}
                {!workspace && <HistoricalVisitsView
                  historicalVisits={historicalVisits}
                  expandedVisit={expandedVisit}
                  handleExpandVisit={handleExpandVisit}
                  selectedVisitId={selectedVisitId}
                  historicalVisitEntries={historicalVisitEntries}
                  handleAddEntry={handleAddEntry}
                  isMobile={isMobile}
                  isAddingNewEntry={isAddingNewEntry}
                  renderEntryContent={renderEntryContent}
                  renderSelectedForm={renderSelectedForm}
                  handleEditEntry={handleEditEntry}
                  handleFlagEntry={onFlagEntry}
                  handleInvalidateEntry={onInvalidateEntry}
                  inPlaceEditingEntryId={inPlaceEditingEntryId}
                  setInPlaceEditingEntryId={setInPlaceEditingEntryId}
                  cancelInPlaceEdit={guardedCancelInPlaceEdit}
                  handleSaveInPlaceEdit={handleSaveInPlaceEdit}
                  refreshHistoricalData={refreshHistoricalData}
                  onNewChartClick={handleNewChartClick}
                  hasActiveVisit={hasActiveVisit}
                  loadingVisits={loadingVisits}
                  hasMoreVisits={hasMoreVisits}
                  currentPage={historicalVisitPage}
                  totalPages={Math.max(1, Math.ceil(loadedHistoricalVisits.length / VISITS_PER_PAGE) + (hasMoreVisits ? 1 : 0))}
                  onPageChange={handleHistoricalVisitPageChange}
                  loadingMoreVisits={loadingMoreVisits}
                />}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Phase 18 M3: sign the chart of the visit just ended */}
      <SubmitChartSheet
        open={submitConfirmOpen}
        onClose={() => setSubmitConfirmOpen(false)}
        entryCount={submitEntryCount}
        busy={submitting}
        error={submitError}
        onConfirm={handleSubmitChart}
      />
      {/* Phase 18 M10: leaving a form with unsaved text */}
      <ConfirmDialog {...unsavedGuard.dialogProps} />

      {/* Phase 18U U17: keyboard approve and the shortcut list */}
      <ConfirmDialog
        open={approveConfirmOpen}
        title="Approve chart"
        message="Approve this chart and mark it complete?"
        confirmLabel="Approve"
        busy={submitting}
        onConfirm={async () => { setApproveConfirmOpen(false); await handleApproveChart(); }}
        onCancel={() => setApproveConfirmOpen(false)}
      />
      <Dialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} labelledBy="review-shortcuts-title">
        <h3 id="review-shortcuts-title" className="mb-3 text-lg font-semibold text-slate-800">Review shortcuts</h3>
        <dl className="grid grid-cols-[6rem_1fr] gap-y-2 text-sm">
          {REVIEW_SHORTCUTS.map(([keys, what]) => (
            <React.Fragment key={keys}><dt className="font-mono font-semibold text-slate-800">{keys}</dt><dd className="text-slate-700">{what}</dd></React.Fragment>
          ))}
        </dl>
      </Dialog>

      {/* Phase 18U U6: everything that can be added to this chart */}
      <AddEntrySheet
        open={addSheetOpen}
        onClose={() => setAddSheetOpen(false)}
        onAdd={(type) => handleAddEntry(type, addVisitId)}
        onCopyLast={(type) => {
          handleAddEntry(type, addVisitId);
          if (copyLast(type)) setCopiedType(type);
        }}
        locked={chartingStatus !== 'DRAFT' || isReadOnly}
        discipline={chartVisit?.discipline}
        visitType={chartVisit?.visitType}
        entries={visitEntries}
        palliative={palliativeActive}
      />
      {/* Phase 18U U2: the visit screen's sticky action bar (Add, End visit, Sign & submit) */}
      {renderActionBar?.(openAddSheet, canAddNow)}

      {/* Invalidate entry: reason required */}
      <ReasonDialog
        open={invalidateTargetId !== null}
        title="Invalidate entry"
        description="The entry stays in the chart, struck through, with your name, the time and this reason."
        label="Reason for invalidating"
        confirmLabel="Invalidate"
        destructive
        busy={invalidating}
        onConfirm={confirmInvalidateEntry}
        onCancel={() => setInvalidateTargetId(null)}
      />

      {/* Amending an entry written before the chart was first submitted: reason required */}
      <ReasonDialog
        open={amendTargetId !== null}
        title="Amend entry"
        description="This entry was part of a signed chart. Your change is saved as an amendment; the original stays readable."
        label="Reason for the amendment"
        confirmLabel="Save amendment"
        busy={saving}
        onConfirm={(reason) => (amendTargetId !== null ? handleSaveInPlaceEdit(amendTargetId, reason) : undefined)}
        onCancel={() => setAmendTargetId(null)}
      />

      {/* Return a SUBMITTED/QA chart for correction: the comment is shown to the author */}
      <ReasonDialog
        open={reopenDialogMode !== null}
        title="Return chart for correction"
        description="The chart returns to draft so the clinician can correct it. Your comment is shown to them and kept in the chart history."
        label="What needs correcting"
        confirmLabel="Return"
        destructive
        busy={reopening}
        onConfirm={handleReopenChart}
        onCancel={() => setReopenDialogMode(null)}
      />

      {/* Error Dialog */}
      <Dialog open={apiError !== null} onClose={() => setApiError(null)} labelledBy="chart-error-title">
        <div className="flex flex-col">
          <div className="flex items-start mb-4">
            <div className="flex-shrink-0 mr-3">
              <div className="p-2 bg-slate-100 rounded-full">
                <AlertTriangle className="h-5 w-5 text-slate-600" />
              </div>
            </div>
            <div className="flex-1">
              <h3 id="chart-error-title" className="text-lg font-medium text-slate-800">Error</h3>
              <p className="mt-1 text-sm text-slate-700">{apiError}</p>
            </div>
          </div>
          <div className="flex justify-end">
            <button
              onClick={() => setApiError(null)}
              className={getButtonClasses(isMobile, "px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-md text-sm")}
            >
              OK
            </button>
          </div>
        </div>
      </Dialog>
    </div>
    </ChartFormContext.Provider>
    </StaffDirectoryContext.Provider>
    </ObservedTimeContext.Provider>
  );
};

export default Charting;
